import { Injectable, signal } from '@angular/core';

import { ProxyWorkflowStatusType } from '../../core/models/proxy-workflow-status-type';

/**
 * Holds the Proxy Queue filter state (status, date range, and search term) so
 * the user's selections persist across in-app navigation — e.g. opening a proxy
 * request detail and returning to the queue. Because the service is provided in
 * root it lives for the whole SPA session; the component binds its signals
 * directly to these. Mirrors NewLinkingRequestsFilterStateService, which backs
 * the equivalent filter bar on the linking queue.
 */
@Injectable({
  providedIn: 'root',
})
export class ProxyVerificationsFilterStateService {
  /**
   * Date range the queue opens on. Owned here rather than on the component
   * because this service holds the default; ProxyVerifications.dateRangeOptions
   * reads it for its "Last 30 Days" entry, so the two cannot drift.
   */
  static readonly DEFAULT_DATE_RANGE_DAYS = 30;

  /** Default: all workflow statuses. */
  readonly statusFilter = signal<ProxyWorkflowStatusType | null>(null);
  /**
   * Days of history to include; null is "All Time" (no lower bound sent to the
   * API).
   */
  readonly dateRangeDays = signal<number | null>(
    ProxyVerificationsFilterStateService.DEFAULT_DATE_RANGE_DAYS,
  );
  readonly searchTerm = signal('');
  /**
   * Search term reflected in the currently loaded data; null when unfiltered.
   * Unlike the selects (which apply live), the search box only commits here
   * when the Filter button is clicked. It lives alongside `searchTerm` so that
   * revisiting the queue reloads exactly what was applied — a term the user
   * typed but never applied must not become applied on the way back in.
   */
  readonly appliedSearchTerm = signal<string | null>(null);
}
